import type { Draft, GroupAnchorDraft } from '@/stores/transportation'

/** Strip the last waypoint id (which marks a departure leg) from the list. */
export function regularFromWaypointIds(
  ids: string[],
  lastWaypointId: string | undefined,
): string[] {
  return ids.filter((id) => id !== lastWaypointId)
}

/**
 * Combine regular legs with an optional departure leg into the
 * `fromWaypointIds` array expected by the server.
 */
export function buildGroupFromWaypointIds(
  regularIds: string[],
  departureEnabled: boolean,
  lastWaypointId: string | undefined,
): string[] {
  const next = new Set(regularFromWaypointIds(regularIds, lastWaypointId))
  if (departureEnabled && lastWaypointId) {
    next.add(lastWaypointId)
  }
  return Array.from(next)
}

/**
 * Selection to show when the user has not touched the LegPicker yet. For a
 * brand-new transportation (no group, not an anchor) we seed the picker with
 * the current waypoint so saving without opening the picker still produces a
 * valid single-leg group candidate.
 */
export function fallbackRegularWaypointIds(params: {
  persistedFromWaypointIds: string[]
  hasExistingGroup: boolean
  lastWaypointId: string | undefined
  waypointId: string | undefined
  seedIsArrival: boolean
  seedIsDeparture: boolean
}): string[] {
  const persistedIds = regularFromWaypointIds(
    params.persistedFromWaypointIds,
    params.lastWaypointId,
  )
  if (
    persistedIds.length === 0 &&
    !params.hasExistingGroup &&
    params.waypointId &&
    !params.seedIsArrival &&
    !params.seedIsDeparture
  ) {
    return [params.waypointId]
  }
  return persistedIds
}

/** Touched draft wins; otherwise fall back to the persisted flag. */
export function effectiveAnchorEnabled(
  draft: Draft<GroupAnchorDraft> | undefined,
  fallback: boolean | undefined,
): boolean {
  if (draft?.touched) {
    return draft.value.enabled
  }
  return !!fallback
}

/**
 * A transportation group must keep at least this many effective legs. Below it
 * the group auto-demotes to a standalone transportation.
 */
export const MIN_GROUP_LEG_COUNT = 2

/**
 * Effective leg count of a (candidate) group: the regular legs plus the
 * optional arrival and departure anchor legs.
 */
export function effectiveGroupLegCount(params: {
  regularLegCount: number
  arrivalEnabled: boolean
  departureEnabled: boolean
}): number {
  return (
    params.regularLegCount +
    (params.arrivalEnabled ? 1 : 0) +
    (params.departureEnabled ? 1 : 0)
  )
}

/** A group with fewer than the minimum effective legs demotes to standalone. */
export function demotesToStandalone(effectiveLegCount: number): boolean {
  return effectiveLegCount < MIN_GROUP_LEG_COUNT
}
